import React, { useContext, useSyncExternalStore } from 'react'
import { ReactReduxContext } from 'react-redux'
import './styles/farmloader.scss'

type FarmLoaderProps = {
  label?: string
  labelColor?: string
  // É o próprio overlay global (LoadingOverlay): nunca se esconde.
  isGlobalOverlay?: boolean
}

// Desfile de animais andando para a direita, um atrás do outro. Cada SVG é
// desenhado num viewBox de altura 60 com o chão em y≈55, e todos usam a mesma
// escala, então os tamanhos ficam proporcionais entre si.
//
// Pernas na marcha diagonal: as classes --a e --b alternam e cada uma junta
// uma perna de trás de um lado com a da frente do outro.

const Hoofed = ({
  x,
  y,
  h,
  w,
  fill,
  hoof,
  pair,
}: {
  x: number
  y: number
  h: number
  w: number
  fill: string
  hoof: string
  pair: 'a' | 'b'
}) => (
  <g className={`farmLoader-leg farmLoader-leg--${pair}`}>
    <rect x={x} y={y} width={w} height={h} rx={2.5} fill={fill} />
    <rect x={x} y={y + h - 4} width={w} height={5} rx={1.5} fill={hoof} />
  </g>
)

const Cow = () => (
  <>
    <Hoofed x={20} y={36} h={18} w={6} fill='#E4E0DB' hoof='#3A3A3A' pair='a' />
    <Hoofed x={52} y={36} h={18} w={6} fill='#E4E0DB' hoof='#3A3A3A' pair='b' />
    <g className='farmLoader-bob'>
      <g className='farmLoader-tail'>
        <path
          d='M13,22 C7,28 6,36 7,42'
          fill='none'
          stroke='#3A3A3A'
          strokeWidth={2}
          strokeLinecap='round'
        />
        <circle cx={7} cy={43} r={2.5} fill='#3A3A3A' />
      </g>
      <rect
        x={10}
        y={16}
        width={48}
        height={26}
        rx={13}
        fill='#FFFFFF'
        stroke='#D9D5D0'
        strokeWidth={1.5}
      />
      <ellipse cx={24} cy={23} rx={7} ry={5} fill='#EA5D0D' />
      <ellipse cx={44} cy={34} rx={5} ry={4} fill='#EA5D0D' />
      <ellipse cx={52} cy={21} rx={4} ry={3} fill='#EA5D0D' />
      <ellipse cx={34} cy={43} rx={4} ry={2.5} fill='#F7B6A3' />
      <ellipse
        cx={60}
        cy={13}
        rx={5}
        ry={2.6}
        fill='#FFFFFF'
        stroke='#D9D5D0'
        strokeWidth={1.5}
        transform='rotate(-20 60 13)'
      />
      <ellipse
        cx={66}
        cy={19}
        rx={9}
        ry={10}
        fill='#FFFFFF'
        stroke='#D9D5D0'
        strokeWidth={1.5}
      />
      <ellipse cx={71} cy={25} rx={6.5} ry={5} fill='#F7B6A3' />
      <circle cx={69.5} cy={25} r={1} fill='#8A4B3C' />
      <circle cx={73} cy={25} r={1} fill='#8A4B3C' />
      <circle cx={66} cy={16} r={1.6} fill='#1A1A1A' />
    </g>
    <Hoofed x={14} y={36} h={18} w={6} fill='#FFFFFF' hoof='#2A2A2A' pair='b' />
    <Hoofed x={46} y={36} h={18} w={6} fill='#FFFFFF' hoof='#2A2A2A' pair='a' />
  </>
)

const Ox = () => (
  <>
    <Hoofed x={20} y={37} h={17} w={7} fill='#6E4530' hoof='#2A2A2A' pair='a' />
    <Hoofed x={56} y={37} h={17} w={7} fill='#6E4530' hoof='#2A2A2A' pair='b' />
    <g className='farmLoader-bob'>
      <g className='farmLoader-tail'>
        <path
          d='M12,21 C6,28 5,36 6,42'
          fill='none'
          stroke='#6E4530'
          strokeWidth={2.2}
          strokeLinecap='round'
        />
        <circle cx={6} cy={43} r={2.6} fill='#2A2A2A' />
      </g>
      <rect x={10} y={15} width={52} height={28} rx={14} fill='#8B5A3C' />
      <ellipse cx={52} cy={15} rx={9} ry={6} fill='#8B5A3C' />
      <path
        d='M63,13 C58,9 58,5 61,3'
        fill='none'
        stroke='#CDBB9E'
        strokeWidth={2.6}
        strokeLinecap='round'
      />
      <ellipse cx={70} cy={21} rx={9.5} ry={10.5} fill='#8B5A3C' />
      <path
        d='M68,12 C67,6 70,3 74,3'
        fill='none'
        stroke='#D8C8AE'
        strokeWidth={2.8}
        strokeLinecap='round'
      />
      <ellipse
        cx={62}
        cy={17}
        rx={5}
        ry={2.6}
        fill='#6E4530'
        transform='rotate(25 62 17)'
      />
      <ellipse cx={75} cy={27} rx={6.5} ry={5} fill='#D9A88F' />
      <circle cx={73.5} cy={27} r={1} fill='#6E4530' />
      <circle cx={77} cy={27} r={1} fill='#6E4530' />
      <circle cx={70} cy={18} r={2.2} fill='#F3EDE4' />
      <circle cx={70.4} cy={18} r={1.3} fill='#1A1A1A' />
    </g>
    <Hoofed x={14} y={37} h={17} w={7} fill='#8B5A3C' hoof='#2A2A2A' pair='b' />
    <Hoofed x={50} y={37} h={17} w={7} fill='#8B5A3C' hoof='#2A2A2A' pair='a' />
  </>
)

const ChickenLeg = ({ x, pair }: { x: number; pair: 'a' | 'b' }) => (
  <g className={`farmLoader-leg farmLoader-leg--${pair}`}>
    <rect x={x} y={42} width={2.5} height={13} rx={1} fill='#E8932F' />
    <rect x={x - 2.5} y={53.5} width={7.5} height={2} rx={1} fill='#E8932F' />
  </g>
)

const Chicken = () => (
  <>
    <ChickenLeg x={18.5} pair='a' />
    <g className='farmLoader-bob farmLoader-bob--quick'>
      <g className='farmLoader-tail farmLoader-tail--up'>
        <path d='M10,32 C3,24 3,15 9,12 C11,20 13,26 16,30 Z' fill='#7A3E1D' />
      </g>
      <ellipse cx={22} cy={34} rx={14} ry={11} fill='#C8642E' />
      <ellipse cx={30} cy={27} rx={6} ry={7} fill='#C8642E' />
      <circle cx={33} cy={22} r={7} fill='#C8642E' />
      <ellipse cx={20} cy={34} rx={8} ry={5.5} fill='#A44E22' />
      <circle cx={31} cy={14.8} r={2.2} fill='#D9342B' />
      <circle cx={34.2} cy={14.2} r={2.4} fill='#D9342B' />
      <circle cx={37.2} cy={15.8} r={2} fill='#D9342B' />
      <path d='M39.5,20.5 L45,23 L39.5,25.5 Z' fill='#F2A93B' />
      <ellipse cx={39} cy={27.5} rx={1.6} ry={2.4} fill='#D9342B' />
      <circle cx={35} cy={20.5} r={1.4} fill='#1A1A1A' />
    </g>
    <ChickenLeg x={24.5} pair='b' />
  </>
)

const Pig = () => (
  <>
    <Hoofed x={20} y={38} h={15} w={7} fill='#E58A9C' hoof='#A85468' pair='a' />
    <Hoofed x={50} y={38} h={15} w={7} fill='#E58A9C' hoof='#A85468' pair='b' />
    <g className='farmLoader-bob'>
      <g className='farmLoader-tail'>
        <path
          d='M11,27 C6,25 4,31 8,32 C11,33 11,28 7,28'
          fill='none'
          stroke='#E58A9C'
          strokeWidth={2}
          strokeLinecap='round'
        />
      </g>
      <rect x={9} y={18} width={50} height={26} rx={13} fill='#F7A8B8' />
      <circle cx={61} cy={27} r={11} fill='#F7A8B8' />
      <path d='M55,17 L59,8 L64,16 Z' fill='#E58A9C' />
      <ellipse cx={71} cy={29} rx={4} ry={5.5} fill='#EE8DA1' />
      <circle cx={71.5} cy={27.3} r={1} fill='#A85468' />
      <circle cx={71.5} cy={30.7} r={1} fill='#A85468' />
      <circle cx={63} cy={24} r={1.6} fill='#1A1A1A' />
    </g>
    <Hoofed x={14} y={38} h={15} w={7} fill='#F29DAE' hoof='#A85468' pair='b' />
    <Hoofed x={44} y={38} h={15} w={7} fill='#F29DAE' hoof='#A85468' pair='a' />
  </>
)

// Lã: os mesmos círculos desenhados duas vezes, o de trás um pouco maior e
// mais escuro, viram o contorno da nuvem sem traço sobreposto entre bolhas.
const WOOL: [number, number, number][] = [
  [18, 26, 9],
  [28, 20, 10],
  [40, 19, 10],
  [51, 23, 9],
  [54, 33, 8],
  [44, 38, 9],
  [30, 38, 9],
  [17, 35, 8],
]

const Sheep = () => (
  <>
    <Hoofed x={21} y={38} h={16} w={5} fill='#555555' hoof='#2A2A2A' pair='a' />
    <Hoofed x={51} y={38} h={16} w={5} fill='#555555' hoof='#2A2A2A' pair='b' />
    <g className='farmLoader-bob'>
      <circle cx={9} cy={28} r={5.5} fill='#DCD3C5' />
      <circle cx={9} cy={28} r={4} fill='#F3EEE6' />
      {WOOL.map(([cx, cy, r]) => (
        <circle key={`o${cx}`} cx={cx} cy={cy} r={r + 1.5} fill='#DCD3C5' />
      ))}
      {WOOL.map(([cx, cy, r]) => (
        <circle key={`i${cx}`} cx={cx} cy={cy} r={r} fill='#F3EEE6' />
      ))}
      <rect x={18} y={22} width={34} height={16} fill='#F3EEE6' />
      <ellipse
        cx={58}
        cy={19}
        rx={4.5}
        ry={2}
        fill='#2A2A2A'
        transform='rotate(-20 58 19)'
      />
      <ellipse cx={64} cy={24} rx={7} ry={8.5} fill='#3A3A3A' />
      <circle cx={61} cy={16} r={4} fill='#F3EEE6' />
      <circle cx={65.5} cy={21.5} r={1.3} fill='#FFFFFF' />
    </g>
    <Hoofed x={16} y={38} h={16} w={5} fill='#3A3A3A' hoof='#1A1A1A' pair='b' />
    <Hoofed x={46} y={38} h={16} w={5} fill='#3A3A3A' hoof='#1A1A1A' pair='a' />
  </>
)

// Ordem do desfile: o primeiro da lista vai na frente.
export const ANIMALS = [
  { name: 'vaca', width: 80, Body: Cow },
  { name: 'boi', width: 84, Body: Ox },
  { name: 'galinha', width: 48, Body: Chicken },
  { name: 'porco', width: 76, Body: Pig },
  { name: 'ovelha', width: 70, Body: Sheep },
]

// 0,9 px por unidade do SVG: 60 unidades de altura viram 54 px.
const SCALE = 0.9
// Tem que bater com a duração de .farmLoader-animal no farmloader.scss.
const MARCH_SECONDS = 8
// Quanto do percurso o primeiro animal já andou quando o loader aparece:
// ~55% o deixa no meio-direito do palco, inteiro à vista, puxando o desfile.
const LEAD_PROGRESS = 0.55

// Delay negativo: o desfile já começa no meio do caminho, cada animal um
// intervalo atrás do anterior. O módulo dá a volta no último da fila, que
// reaparece logo à frente do primeiro, saindo do palco.
export const marchDelay = (index: number) => {
  const spacing = MARCH_SECONDS / ANIMALS.length
  const lead = LEAD_PROGRESS * MARCH_SECONDS
  const elapsed = (lead - index * spacing + MARCH_SECONDS) % MARCH_SECONDS
  return -Number(elapsed.toFixed(2))
}

const noopSubscribe = () => () => {}

// Se o overlay global (state.screen.loading, ver LoadingOverlay) está aberto.
// Lê o store pelo contexto, sem exigir <Provider>: fora dele (testes, telas
// isoladas) responde `false`.
export const useGlobalOverlayOpen = () => {
  const store = useContext(ReactReduxContext)?.store
  return useSyncExternalStore(store ? store.subscribe : noopSubscribe, () =>
    Boolean(store?.getState()?.screen?.loading),
  )
}

const FarmLoader = ({
  label = 'Carregando...',
  labelColor = '#FFFFFF',
  isGlobalOverlay = false,
}: FarmLoaderProps) => {
  // Navegar de uma tela que ligou o overlay global para outra que já monta
  // carregando (withPropsFromDatabase, Suspense, loader próprio da página)
  // mostrava dois loaders ao mesmo tempo, um atrás do fundo semitransparente.
  // Enquanto o overlay estiver aberto, só ele aparece. `visibility` (e não
  // desmontar) mantém o espaço da página, sem pulo de layout quando ele fecha.
  const hidden = useGlobalOverlayOpen() && !isGlobalOverlay

  return (
    <div
      role='status'
      aria-live='polite'
      aria-hidden={hidden || undefined}
      className='farmLoader'
      style={hidden ? { visibility: 'hidden' } : undefined}
    >
      <div
        className='farmLoader-stage'
        role='progressbar'
        aria-label='Progresso indeterminado'
      >
        {ANIMALS.map(({ name, width, Body }, i) => (
          <div
            key={name}
            className='farmLoader-animal'
            data-animal={name}
            style={{ animationDelay: `${marchDelay(i)}s` }}
          >
            <svg
              viewBox={`0 0 ${width} 60`}
              width={width * SCALE}
              height={60 * SCALE}
              aria-hidden='true'
              focusable='false'
            >
              <Body />
            </svg>
          </div>
        ))}
      </div>

      <span className='farmLoader-label' style={{ color: labelColor }}>
        {label}
      </span>
    </div>
  )
}

export default FarmLoader
